import {
  DocumentReference,
  ISoftDelete,
  ITimestampRange,
  Timestamp,
} from '@principle-theorem/shared';
import { IBrand } from './brand';
import { IStaffer } from './staffer/staffer';
import { IPractice } from './practice/practice';

export enum ReportingJobType {
  AccountCreditsUsedFromOtherPractice = 'reporting.account-credits-used-from-other-practice',
  AccountCreditsUsedUpUntilDate = 'reporting.account-credits-used-up-until-date',
  AppointmentsWithoutClinicalNote = 'reporting.appointment-without-clinical-notes',
  ContactReferralSummary = 'reporting.contact-referral-summary',
  CreditNotesByPractice = 'reporting.credit-notes-by-practice',
  DebtorsAsOfDate = 'reporting.debtors-as-of-date',
  DuplicatePatientContactDetails = 'reporting.duplicate-patient-contact-details',
  IncompleteTreatment = 'reporting.incomplete-treatment',
  InvoiceLineItemPaymentAllocations = 'reporting.invoice-line-item-payment-allocation',
  InvoiceLineItemTax = 'reporting.invoice-line-item-tax',
  OnlineBookings = 'reporting.online-bookings',
  PractitionerFTAVsUTARates = 'reporting.practitioner-fta-vs-uta-rates',
  PractitionerGaps = 'reporting.practitioner-gaps',
  PractitionerRebookingRates = 'reporting.practitioner-rebooking-rates',
}

export interface IReportingJobDisplay {
  type: ReportingJobType;
  description: string;
  name: string;
}

export const REPORTING_JOBS_DISPLAY = [
  {
    type: ReportingJobType.AccountCreditsUsedUpUntilDate,
    description:
      'Shows a list of account credit transactions that were used up until the selected date range',
    name: 'Account Credits Used Up Until Date',
  },
  {
    type: ReportingJobType.ContactReferralSummary,
    description:
      'Exports all contacts with their phone, email, address, and the number of patients they have referred. Includes a detailed sheet showing which patients were referred by each contact.',
    name: 'Contact Referral Summary',
  },
  {
    type: ReportingJobType.AppointmentsWithoutClinicalNote,
    description:
      'List of appointments missing clinical notes. These are displayed by patient, practice, practitioner, and treatment',
    name: 'Appointments Without Clinical Notes',
  },
  {
    type: ReportingJobType.PractitionerFTAVsUTARates,
    description:
      'Displays the FTA and UTA rates for each practitioner by week. Please note: FTA is calculated as less than 24 hours from the appointment, and UTA is up to 48 hours before the appointment',
    name: 'Practitioner FTA vs UTA Rates',
  },
  {
    type: ReportingJobType.OnlineBookings,
    description:
      'Displays the number of online booking requests, grouped by week and practice',
    name: 'Online Bookings',
  },
  {
    type: ReportingJobType.PractitionerGaps,
    description:
      'The first sheet shows the gap vs rostered time for each practitioner by date. The second sheet is an overall time utilisation summary for the given time range',
    name: 'Practitioner Gaps',
  },
  {
    type: ReportingJobType.PractitionerRebookingRates,
    description:
      'Rebooking rate is calculated by the number of next appointments booked vs the total appointment count for each practitioner',
    name: 'Practitioner Rebooking Rates',
  },
  {
    type: ReportingJobType.AccountCreditsUsedFromOtherPractice,
    description:
      'Shows a list of account credit transactions where the account credit was originally paid for at another practice',
    name: 'Account Credits Used From Other Practice',
  },
  {
    type: ReportingJobType.DebtorsAsOfDate,
    description:
      'Shows a list of outstanding invoices as they were at the end of the selected date range. This includes backdated transactions that fall within the selected date range',
    name: 'Debtors as of date',
  },
  {
    type: ReportingJobType.DuplicatePatientContactDetails,
    description:
      'Lists patients that share an email address or phone number, with the relationship recorded against each match, so missing relationships can be recorded and duplicate patient records identified and merged.',
    name: 'Duplicate Patient Contact Details',
  },
  {
    type: ReportingJobType.IncompleteTreatment,
    description:
      'Shows a list of incomplete treatments that were created within the selected date range',
    name: 'Incomplete Treatment',
  },
  {
    type: ReportingJobType.InvoiceLineItemPaymentAllocations,
    description:
      'Shows a list of invoice line items and the payments that have been allocated to them. Similar to the Practitioner income by transaction date report, but with more detail',
    name: 'Invoice Line Item Payment Allocations',
  },
  {
    type: ReportingJobType.InvoiceLineItemTax,
    description:
      'Shows a list of invoice line items and their tax components from invoices created within the selected date range.',
    name: 'Invoice Line Item Tax',
  },
  {
    type: ReportingJobType.CreditNotesByPractice,
    description:
      'Shows a list of credit note transactions by practice, including the transaction date, and transaction amount.',
    name: 'Credit Note Transactions',
  },
];

export enum ReportingJobFileType {
  Csv = 'csv',
  Xlsx = 'xlsx',
}

export enum ReportingJobStatus {
  Pending = 'pending',
  InProgress = 'inProgress',
  Complete = 'complete',
  Failed = 'failed',
}

export interface IReportingJob {
  requestParams: IReportJobParams;
  type: ReportingJobType;
  status: ReportingJobStatus;
  file?: {
    name: string;
    path: string;
  };
  /**
   * Number of times the runner has started generating this report. Used to cap
   * retries after an attempt dies without reporting back (e.g. out of memory).
   */
  attempts?: number;
  /**
   * When the current attempt started. Pub/Sub redelivers messages that are not
   * acknowledged within 10 minutes, so this lets the runner tell a live attempt
   * apart from one that has crashed.
   */
  attemptStartedAt?: Timestamp;
  /**
   * Refreshed periodically while an attempt is running. If it goes stale the
   * attempt has died (out of memory or timeout) and the job can be recovered.
   */
  attemptHeartbeatAt?: Timestamp;
}

export interface IReportJobParams {
  range: ITimestampRange;
  brandRef: DocumentReference<IBrand>;
  practiceRef?: DocumentReference<IPractice>;
  fileType: ReportingJobFileType;
}

export interface IPractitionerFTAReportParams {
  skipPractitioners: DocumentReference<IStaffer>[];
}

export interface IPractitionerGapsJobParams {
  skipPractitioners: DocumentReference<IStaffer>[];
  groupRecordsBy: 'day' | 'week';
}

export type ReportingJob = IReportingJob & ISoftDelete;

export type PractitionerFTAReportJob = ReportingJob & {
  type: ReportingJobType.PractitionerFTAVsUTARates;
  requestParams: IReportJobParams & IPractitionerFTAReportParams;
};

export type PractitionerGapsJob = ReportingJob & {
  type: ReportingJobType.PractitionerGaps;
  requestParams: IReportJobParams & IPractitionerGapsJobParams;
};
